import type { DesignSpec } from '@/types';
import { getFormat } from '@/lib/formats';
import { renderToCanvas } from './renderer';

/**
 * Export.
 *
 * Every export re-renders from the DesignSpec at the target resolution rather
 * than upscaling the preview, so a 2x or 4x file is genuinely sharper — type
 * is re-rasterized at the larger size, not stretched.
 */

export type ExportFormat = 'png' | 'jpg' | 'png-transparent';

export interface ExportOptions {
  format: ExportFormat;
  /** Multiplier over the format's native pixel size. */
  scale: number;
  quality: number;
}

export interface ExportResult {
  blob: Blob;
  filename: string;
  width: number;
  height: number;
}

function slugify(value: string): string {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 48) || 'design'
  );
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  mime: string,
  quality: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Could not encode the image.'))),
      mime,
      quality,
    );
  });
}

export async function exportDesign(
  spec: DesignSpec,
  photo: HTMLImageElement | null,
  logo: HTMLImageElement | null,
  options: ExportOptions,
): Promise<ExportResult> {
  const format = getFormat(spec.format);
  const width = Math.round(format.width * options.scale);
  const height = Math.round(format.height * options.scale);

  // A transparent export drops the photo but keeps overlays, type and logo —
  // the layer you'd composite over footage or another background.
  const includeImage = options.format !== 'png-transparent';

  const canvas = await renderToCanvas(spec, photo, logo, width, height, includeImage);

  const mime = options.format === 'jpg' ? 'image/jpeg' : 'image/png';
  const blob = await canvasToBlob(canvas, mime, options.quality);
  const ext = options.format === 'jpg' ? 'jpg' : 'png';
  const suffix = options.format === 'png-transparent' ? '-transparent' : '';

  const headline = spec.blocks.find((b) => b.role === 'headline')?.text ?? spec.name;

  return {
    blob,
    filename: `${slugify(headline)}-${format.short.replace(':', 'x')}-${width}x${height}${suffix}.${ext}`,
    width,
    height,
  };
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoke on the next tick so the download has definitely started.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Small preview render for the variant thumbnails. */
export async function renderThumbnail(
  spec: DesignSpec,
  photo: HTMLImageElement | null,
  logo: HTMLImageElement | null,
  maxEdge = 260,
): Promise<string> {
  const format = getFormat(spec.format);
  const scale = maxEdge / Math.max(format.width, format.height);
  const canvas = await renderToCanvas(
    spec,
    photo,
    logo,
    Math.round(format.width * scale),
    Math.round(format.height * scale),
    true,
  );
  return canvas.toDataURL('image/jpeg', 0.82);
}
